import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import qlog from "views_shared/app/javascript/scripts/qlog";
import { Tab, Tabs } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

import CodeEditor from "./CodeEditor";
import TopAgencyTable from "./TopAgencyTable";
import AddAgencyModal from "./AddAgencyModal";

// Constants for API URLs
const topAgenciesUrl = "/interact/api/agency_analytics?period=:period&limit=80&prs_only=true&order=desc";
const entryUrl = "/admin/qwoted_agency_fifties/:id.json";
const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
const supabaseUrl = globalThis.Qwoted?.SUPABASE_API_EXTERNAL_URL;

window.DI__QwotedAgencyFifty = {
  fetchTagsForAgencies: async (companyIds) => {
    if (!companyIds.length) return [];

    const params = new URLSearchParams({
      profile_type: "eq.Id::PromoEntity::Company",
      profile_id: `in.(${companyIds.join(",")})`,
      select: "profile_id,hashtag_name,hashtag_usage_count",
    });

    const response = await fetch(`${supabaseUrl}/rest/v1/derived_tags?${params}`, {
      cache: "no-store",
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  },

  fetchQwotedAgencyFifty: async (period) => {
    const response = await fetch(topAgenciesUrl.replace(":period", period), {
      cache: "no-store",
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  },

  fetchCurrentQwotedAgencyFifty: async () => {
    const period = globalThis.Qwoted.BUSINESS_ENVIRONMENT === "development" ? "all_time" : "this_quarter";
    return window.DI__QwotedAgencyFifty.fetchQwotedAgencyFifty(period);
  },

  fetchPreviousQwotedAgencyFifty: async () => {
    // return window.DI__QwotedAgencyFifty.fetchQwotedAgencyFifty("all_time"); // useful for development DONTCOMMIT uncommented
    return window.DI__QwotedAgencyFifty.fetchQwotedAgencyFifty("last_quarter");
  },
};

// Select top 5 tags per company by global usage count
/* eslint-disable camelcase */
const groupTop5TagsByCompany = (tags) => {
  const tagsByCompany = tags.reduce((acc, tag) => {
    const { profile_id, hashtag_name, hashtag_usage_count } = tag;
    if (!acc[profile_id]) acc[profile_id] = [];
    acc[profile_id].push({
      name: hashtag_name,
      usage_count: hashtag_usage_count,
    });
    return acc;
  }, {});

  Object.keys(tagsByCompany).forEach((companyId) => {
    tagsByCompany[companyId] = tagsByCompany[companyId]
      .sort((a, b) => b.usage_count - a.usage_count)
      .slice(0, 5)
      .map(({ name }) => name);
  });

  return tagsByCompany;
};
/* eslint-enable camelcase */

// Hook: Manages the state and operations of a Qwoted Agency Fifty entity
// (includes communication with the backend)
function useQwotedAgencyFiftyEntityData(id) {
  const [entity, setEntity] = useState(null); // Current entity data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const url = entryUrl.replace(":id", id.toString());

  // Fetches the entity data
  const fetchEntity = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setEntity(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url]);

  // Saves updates to the entity payload
  const saveEntity = async (updatedPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ qwoted_agency_fifty: { payload: updatedPayload } }),
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const updatedEntity = await response.json();
      setEntity(updatedEntity);
    } catch (err) {
      setError(`Failed to save data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reloads entity payload with new data from top agencies
  // (We accept a "fetchFn" argument so we can fetch either current or last quarter.)
  const reloadEntityFromTopAgencies = async (fetchFn) => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchFn();
      const loadedTopAgencies = json.data.agencies.map((agency) => ({ ...agency, hidden: false }));

      const companyIds = loadedTopAgencies.map((a) => a.company_id);
      const allTags = await window.DI__QwotedAgencyFifty.fetchTagsForAgencies(companyIds);
      const top5TagsByCompany = groupTop5TagsByCompany(allTags);

      const agenciesWithTags = loadedTopAgencies.map((agency) => ({
        ...agency,
        tags: top5TagsByCompany[agency.company_id] || [],
      }));

      await saveEntity(agenciesWithTags);
    } catch (err) {
      setError(`Failed to load new data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetches the entity data on component mount or when `id` changes
  useEffect(() => {
    fetchEntity();
  }, [id, fetchEntity]);

  return { entity, loading, error, saveEntity, reloadEntityFromTopAgencies };
}

// Main Component: Manages Qwoted Agency Fifty entity details and UI
function QwotedAgencyFifty({ id }) {
  const { entity, loading, error, saveEntity, reloadEntityFromTopAgencies } = useQwotedAgencyFiftyEntityData(id);
  const [activeTab, setActiveTab] = useState("visual"); // Active tab state
  const [showAddModal, setShowAddModal] = useState(false); // Modal visibility state

  // Saves changes to the top agencies
  const handleTopAgenciesChange = async (newTopAgencies) => {
    await saveEntity(newTopAgencies);
  };

  // Repopulates the payload from either "current" or "previous" quarter
  const handleTopAgenciesRepopulate = async (fetchFn) => {
    if (
      entity.payload &&
      entity.payload.length > 0 &&
      !window.confirm("This will overwrite the current list. Are you sure?") // eslint-disable-line no-alert
    ) {
      return;
    }

    await reloadEntityFromTopAgencies(fetchFn);
  };

  // Toggles the visibility of a row in the top agencies table
  const toggleRowVisibility = (companyId) => {
    const updatedPayload = entity.payload.map((entry) =>
      entry.company_id === companyId ? { ...entry, hidden: !entry.hidden } : entry,
    );
    saveEntity(updatedPayload);
  };

  // Moves an item up in the list (swaps with previous item)
  const moveItemUp = (companyId) => {
    const idx = entity.payload.findIndex((entry) => entry.company_id === companyId);
    if (idx <= 0) return;

    const updatedPayload = [...entity.payload];
    [updatedPayload[idx - 1], updatedPayload[idx]] = [updatedPayload[idx], updatedPayload[idx - 1]];
    saveEntity(updatedPayload);
  };

  // Moves an item down in the list (swaps with next item)
  const moveItemDown = (companyId) => {
    const idx = entity.payload.findIndex((entry) => entry.company_id === companyId);
    if (idx < 0 || idx >= entity.payload.length - 1) return;

    const updatedPayload = [...entity.payload];
    [updatedPayload[idx], updatedPayload[idx + 1]] = [updatedPayload[idx + 1], updatedPayload[idx]];
    saveEntity(updatedPayload);
  };

  // Deletes an item from the list
  const deleteItem = (companyId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Are you sure you want to remove this agency from the list?")) {
      return;
    }

    const updatedPayload = entity.payload.filter((entry) => entry.company_id !== companyId);
    saveEntity(updatedPayload);
  };

  // Updates tags for an agency
  const updateTags = (companyId, newTags) => {
    const updatedPayload = entity.payload.map((entry) =>
      entry.company_id === companyId ? { ...entry, tags: newTags } : entry,
    );
    saveEntity(updatedPayload);
  };

  const addItem = async (agencyData, position) => {
    try {
      let tags = [];
      try {
        const tagsData = await window.DI__QwotedAgencyFifty.fetchTagsForAgencies([agencyData.company_id]);
        const top5TagsByCompany = groupTop5TagsByCompany(tagsData);
        tags = top5TagsByCompany[agencyData.company_id] || [];
      } catch (tagError) {
        // Log but continue - agency can be added without tags
        qlog.warn("Failed to fetch tags for agency:", tagError);
      }

      const newEntry = {
        ...agencyData,
        hidden: false,
        tags,
      };

      let updatedPayload;
      if (position !== undefined && position > 0 && position <= entity.payload.length + 1) {
        const insertIndex = position - 1;
        updatedPayload = [...entity.payload.slice(0, insertIndex), newEntry, ...entity.payload.slice(insertIndex)];
      } else {
        updatedPayload = [...entity.payload, newEntry];
      }

      await saveEntity(updatedPayload);
    } catch (err) {
      qlog.error("Failed to add agency:", err);
    }
  };

  // Gets list of existing company IDs for duplicate detection
  const existingCompanyIds = entity?.payload?.map((entry) => entry.company_id) || [];

  // Reloads the page
  const pageReload = () => {
    window.location.reload();
  };

  // Displays a loading state if data is not ready (e.g. on the first load)
  if (loading && (!entity || !entity.payload)) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  // Displays an error state with a reload option
  if (error) {
    return (
      <div className="text-center text-danger mt-5">
        <p>Error: {error}</p>
        <p>
          <button type="button" className="btn btn-link" onClick={pageReload}>
            Reload the page
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">
          <a href="/admin/qwoted_agency_fifties">
            <i className="fa-solid fa-arrow-left" />
          </a>
          &nbsp;
          {entity.title}
        </h1>
        <button type="button" className="btn btn-success" onClick={() => setShowAddModal(true)}>
          <i className="fas fa-plus me-1" />
          Add Agency
        </button>
      </div>

      <AddAgencyModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={addItem}
        existingCompanyIds={existingCompanyIds}
        listSize={entity?.payload?.length || 0}
      />

      <div className={loading ? "opacity-25 pe-none" : ""}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mt-4">
          <Tab eventKey="visual" title="Visual">
            <TopAgencyTable
              agencies={entity.payload}
              toggleRowVisibility={toggleRowVisibility}
              moveItemUp={moveItemUp}
              moveItemDown={moveItemDown}
              deleteItem={deleteItem}
              updateTags={updateTags}
            />
          </Tab>
          <Tab eventKey="code" title="Code">
            <div>
              <CodeEditor topAgencies={entity.payload} onTopAgenciesChanged={handleTopAgenciesChange} />
            </div>
          </Tab>
        </Tabs>
        <div className="mt-3">
          <button type="button" className="btn btn-success me-3" onClick={() => setShowAddModal(true)}>
            <i className="fas fa-plus me-1" />
            Add Agency
          </button>
          <button
            type="button"
            className="btn btn-warning me-3"
            onClick={() => handleTopAgenciesRepopulate(window.DI__QwotedAgencyFifty.fetchPreviousQwotedAgencyFifty)}
          >
            Load from previous quarter Qwoted Agency 50
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => handleTopAgenciesRepopulate(window.DI__QwotedAgencyFifty.fetchCurrentQwotedAgencyFifty)}
          >
            Load from this quarter Qwoted Agency 50
          </button>
        </div>
      </div>
    </div>
  );
}

QwotedAgencyFifty.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Entity ID
};

export default QwotedAgencyFifty;
